import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import useSeo from '@/hooks/useSeo';
import { articles, categories } from '@/mocks/siteData';

type SortKey = 'new' | 'hot';

export default function CategoryPage() {
  const { slug = 'all' } = useParams();
  const [keyword, setKeyword] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('new');

  const activeCategory = categories.find((cat) => cat.slug === slug);
  const isAll = slug === 'all';
  const title = isAll ? '最新爆料' : activeCategory?.name ?? '最新爆料';
  const description = isAll
    ? '汇总站内全部爆料整理，按发布时间与围观热度排序，最新进展一目了然。'
    : activeCategory?.description ?? '';

  const filtered = useMemo(() => {
    const byCategory = isAll ? articles : articles.filter((item) => item.category === slug);
    const kw = keyword.trim().toLowerCase();
    const byKeyword = kw
      ? byCategory.filter(
          (item) =>
            item.title.toLowerCase().includes(kw) ||
            item.excerpt.toLowerCase().includes(kw) ||
            item.tags.some((tag) => tag.toLowerCase().includes(kw)),
        )
      : byCategory;
    return [...byKeyword].sort((a, b) =>
      sortKey === 'hot' ? b.hot - a.hot : a.date < b.date ? 1 : -1,
    );
  }, [slug, keyword, sortKey, isAll]);

  const crumbs = [
    { label: '首页', to: '/' },
    ...(isAll ? [{ label: '最新爆料' }] : [{ label: '最新爆料', to: '/category/all' }, { label: title }]),
  ];

  useSeo({
    title: `${title} - 爆料吃瓜${isAll ? '最新爆料进展' : '话题整理'}`,
    description: `${description}爆料吃瓜持续更新最新进展，围观现场一手整理。`,
    keywords: `爆料吃瓜,${title},爆料吃瓜现场,爆料吃瓜最新进展`,
    path: `/category/${slug}`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${title} - 爆料吃瓜`,
        description,
        inLanguage: 'zh-CN',
        url: `https://rudolf1922.cn/category/${slug}`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: crumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.label,
          item: crumb.to ? `https://rudolf1922.cn${crumb.to}` : undefined,
        })),
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={crumbs} />
          <div className="mt-4 flex items-start gap-4">
            <span className="hidden h-12 w-12 items-center justify-center rounded-md bg-primary-500 text-background-50 sm:flex">
              <i className={`${isAll ? 'ri-fire-line' : activeCategory?.icon ?? 'ri-fire-line'} text-2xl`}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">{title}</h1>
              <p className="mt-2 max-w-3xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
                {description}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div role="tablist" aria-label="话题分类" className="inline-flex flex-wrap gap-1 rounded-full bg-background-200 p-1">
            <Link
              to="/category/all"
              role="tab"
              aria-selected={isAll}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 font-label text-sm transition-colors ${
                isAll ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                role="tab"
                aria-selected={cat.slug === slug}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 font-label text-sm transition-colors ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索标题、标签或摘要"
                aria-label="搜索爆料内容"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 font-label text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 sm:w-64"
              />
            </div>
            <div className="inline-flex rounded-md border border-background-300 bg-background-50 p-1">
              <button
                type="button"
                onClick={() => setSortKey('new')}
                className={`whitespace-nowrap rounded px-3 py-1.5 font-label text-sm transition-colors ${
                  sortKey === 'new' ? 'bg-secondary-500 text-background-50' : 'text-foreground-700'
                }`}
              >
                最新
              </button>
              <button
                type="button"
                onClick={() => setSortKey('hot')}
                className={`whitespace-nowrap rounded px-3 py-1.5 font-label text-sm transition-colors ${
                  sortKey === 'hot' ? 'bg-secondary-500 text-background-50' : 'text-foreground-700'
                }`}
              >
                最热
              </button>
            </div>
          </div>
        </div>

        <p className="mt-5 font-label text-sm text-foreground-500">
          共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 条整理
        </p>

        {filtered.length > 0 ? (
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-file-search-line text-4xl text-foreground-400"></i>
            <p className="mt-3 font-heading text-lg font-semibold text-foreground-800">没有找到相关整理</p>
            <p className="mt-1 font-body text-sm text-foreground-500">换个关键词试试，或看看其他话题分类。</p>
            <button
              type="button"
              onClick={() => setKeyword('')}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              清空搜索
            </button>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}